import { INTERNATIONAL_PRICE_HIKE_PERCENT } from "@/lib/config";

export { INTERNATIONAL_PRICE_HIKE_PERCENT };

export interface IProductPriceTier {
  ID?: number;
  ProductID?: number;
  Qty: number;
  Price: number;
}

export interface IPriceSlab {
  rangeLabel: string;
  price: number;
  minQty: number;
  maxQty: number | null;
  isBase?: boolean;
}

/**
 * Calculates effective price after applying international hike if isInternational is true.
 * e.g. price = 100, hike = 15% -> 115
 */
export const getEffectivePrice = (
  price: number,
  isInternational: boolean = false
): number => {
  if (!isInternational || !INTERNATIONAL_PRICE_HIKE_PERCENT || !price) {
    return price;
  }
  const factor = 1 + INTERNATIONAL_PRICE_HIKE_PERCENT / 100;
  return Number((price * factor).toFixed(2));
};

/**
 * Maps tiered price slabs to apply international price hike if isInternational is true.
 */
export const getEffectiveProductPrices = (
  productPrices?: IProductPriceTier[] | null,
  isInternational: boolean = false
): IProductPriceTier[] | undefined => {
  if (!productPrices) return undefined;
  if (!isInternational || !INTERNATIONAL_PRICE_HIKE_PERCENT) return productPrices;
  return productPrices.map((tier) => ({
    ...tier,
    Price: getEffectivePrice(tier.Price, true),
  }));
};

/**
 * Calculates unit price based on ProductPrices tier slabs and selected quantity.
 * Tiers take priority based on Qty thresholds. If productPrices is empty or qty < min tier qty, falls back to basePrice.
 * If isInternational is true, applies price hike to basePrice and tier prices.
 */
export const getProductUnitPrice = (
  basePrice: number,
  productPrices?: IProductPriceTier[] | null,
  qty: number = 1,
  isInternational: boolean = false
): number => {
  const effectiveBase = getEffectivePrice(basePrice, isInternational);
  if (!productPrices || productPrices.length === 0) {
    return effectiveBase;
  }

  const sortedTiers = [...productPrices].sort((a, b) => a.Qty - b.Qty);

  let unitPrice = effectiveBase;
  for (const tier of sortedTiers) {
    if (qty >= tier.Qty) {
      unitPrice = getEffectivePrice(tier.Price, isInternational);
    } else {
      break;
    }
  }

  return unitPrice;
};

/**
 * Generates displayable price slabs (e.g. 1-4, 5-19, 20-99, 100+) for product detail UI.
 * If isInternational is true, slabs reflect the hiked prices.
 */
export const getPriceSlabs = (
  basePrice: number,
  productPrices?: IProductPriceTier[] | null,
  isInternational: boolean = false
): IPriceSlab[] => {
  const effectiveBase = getEffectivePrice(basePrice, isInternational);
  if (!productPrices || productPrices.length === 0) {
    return [];
  }

  const sortedTiers = [...productPrices].sort((a, b) => a.Qty - b.Qty);
  const slabs: IPriceSlab[] = [];

  const firstTierMin = sortedTiers[0].Qty;

  // If first tier starts at > 1, add base price range for 1 to (firstTierMin - 1)
  if (firstTierMin > 1) {
    slabs.push({
      rangeLabel: firstTierMin - 1 === 1 ? "1" : `1-${firstTierMin - 1}`,
      price: effectiveBase,
      minQty: 1,
      maxQty: firstTierMin - 1,
      isBase: true,
    });
  }

  for (let i = 0; i < sortedTiers.length; i++) {
    const currentTier = sortedTiers[i];
    const nextTier = sortedTiers[i + 1];
    const tierPrice = getEffectivePrice(currentTier.Price, isInternational);

    if (nextTier) {
      const maxQty = nextTier.Qty - 1;
      slabs.push({
        rangeLabel:
          currentTier.Qty === maxQty
            ? `${currentTier.Qty}`
            : `${currentTier.Qty}-${maxQty}`,
        price: tierPrice,
        minQty: currentTier.Qty,
        maxQty: maxQty,
      });
    } else {
      slabs.push({
        rangeLabel: `${currentTier.Qty}+`,
        price: tierPrice,
        minQty: currentTier.Qty,
        maxQty: null,
      });
    }
  }

  return slabs;
};

/**
 * Resolves effective base price and product price tiers for a cart item,
 * ensuring international hike is applied exactly once and preventing double-hikes.
 */
export const getCartItemEffectivePricing = (
  item: {
    RawBasePrice?: number;
    BasePrice?: number;
    Price?: number;
    RawProductPrices?: IProductPriceTier[];
    ProductPrices?: IProductPriceTier[];
  },
  isInternational: boolean
): {
  basePrice: number;
  productPrices?: IProductPriceTier[];
} => {
  let rawBase = item.RawBasePrice;

  // Auto-heal double-hiked or un-hiked legacy items when RawBasePrice is missing:
  if (!rawBase && item.BasePrice) {
    if (isInternational && INTERNATIONAL_PRICE_HIKE_PERCENT) {
      const singleHikeFactor = 1 + INTERNATIONAL_PRICE_HIKE_PERCENT / 100;
      const doubleHikeFactor = singleHikeFactor * singleHikeFactor;
      const possibleRawFromDouble = Number(
        (item.BasePrice / doubleHikeFactor).toFixed(2),
      );
      // If item.BasePrice is close to raw * 1.15 * 1.15 (e.g. 171.92 / 1.3225 = 130)
      if (
        Math.abs(possibleRawFromDouble - Math.round(possibleRawFromDouble)) <
        0.08
      ) {
        rawBase = Math.round(possibleRawFromDouble);
      } else {
        const possibleRawFromSingle = Number(
          (item.BasePrice / singleHikeFactor).toFixed(2),
        );
        if (
          Math.abs(possibleRawFromSingle - Math.round(possibleRawFromSingle)) <
          0.08
        ) {
          rawBase = Math.round(possibleRawFromSingle);
        } else {
          rawBase = item.BasePrice;
        }
      }
    } else {
      rawBase = item.BasePrice;
    }
  }

  const basePrice = rawBase
    ? getEffectivePrice(rawBase, isInternational)
    : (item.BasePrice || item.Price || 0);

  const productPrices = item.RawProductPrices
    ? getEffectiveProductPrices(item.RawProductPrices, isInternational)
    : item.ProductPrices;

  return { basePrice, productPrices };
};

